import { useState, useEffect, useCallback, useRef } from 'react';
import { useReactiveVar } from '@apollo/client';
import { toast } from 'react-toastify';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL, fetchFile } from '@ffmpeg/util';

import * as E from '@pages/Equipment/Equipment.style';
import theme from '@styles/theme';
import { mediaListHeader } from '@lib/data';
import { didInfoVar } from '@store/index';
import { PLAYLIST_DATA } from '@lib/DUMMY';
import useFileUpload from '@hooks/useFileUpload';
import { IMAGE_LIMIT_SIZE_20MB } from '@lib/constant';

import { RegularFont } from '@components/styled/StyledComponents';
import TableHeader from '@components/share/Table/TableHeader';
import { EmptyTable, TableBody } from '@components/share/Table/table.style';
import { InfoSection } from '@components/share/Info/InfoSection';
import StyledFileUpload from '@components/styled/StyledFileUpload';
import MediaListRow from '@components/features/Setting/ImageSetting//MediaListRow';
import Loader from '@components/share/Loader';

const VideoSetting = () => {
    const { file, setFile, previewUrl, setPreviewUrl, onChangeFile } =
        useFileUpload({ limitSize: IMAGE_LIMIT_SIZE_20MB });
    const ffmpegRef = useRef(new FFmpeg());
    const { version, type } = useReactiveVar(didInfoVar);
    const [videos, setVideos] = useState<Record<string, boolean>>({});
    const [videoType, setVideoType] = useState('');

    const [isTrimming, setIsTrimming] = useState(false);
    const [trimmedBlob, setTrimmedBlob] = useState<Blob | null>(null);
    const [trimmedUrl, setTrimmedUrl] = useState<string | null>(null);

    const [aspect, setAspect] = useState<number>(16 / 9);

    useEffect(() => {
        if (version === 1) {
            if (type === 3) {
                setVideoType('정방형');
                setAspect(1);
            } else if (type === 4) {
                setVideoType('전면');
                setAspect(9 / 16);
            } else {
                setVideoType('배너');
                setAspect(16 / 9);
            }
        } else if (version === 2) {
            setVideoType('전면');
            setAspect(9 / 16);
        } else if (version === 3) {
            setVideoType('정방형');
            setAspect(1);
        }
    }, [version, type]);

    useEffect(() => {
        if (file) {
            if (
                !window.confirm(
                    '단위 길이를 초과하는 영상은 단위에 맞춰 잘라서 업로드됩니다. 업로드하시겠습니까? (단위: 15초, 20초, 25초, 30초)'
                )
            )
                return;
            const video = document.createElement('video');
            video.src = URL.createObjectURL(file);
            video.onloadedmetadata = () => {
                const flooredDuration = getNearestDuration(video.duration);
                handleTrim(getNearestDuration(flooredDuration));
            };
        }
    }, [file]);

    const load = async () => {
        const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
        const ffmpeg = ffmpegRef.current;
        await ffmpeg.load({
            coreURL: await toBlobURL(
                `${baseURL}/ffmpeg-core.js`,
                'text/javascript'
            ),
            wasmURL: await toBlobURL(
                `${baseURL}/ffmpeg-core.wasm`,
                'application/wasm'
            )
        });
    };

    const getNearestDuration = (duration: number) => {
        if (duration <= 15) return duration;
        if (duration > 30) return 30;

        const floored = Math.floor(duration / 5) * 5;
        return Math.min(30, Math.max(15, floored));
    };

    const handleTrim = async (duration: number) => {
        const ffmpeg = ffmpegRef!.current;
        if (!file) return;
        if (!ffmpeg.loaded) await ffmpeg.load();

        setIsTrimming(true);

        try {
            ffmpeg.writeFile('input.mp4', await fetchFile(file));
            await ffmpeg.exec([
                '-ss',
                '0',
                '-i',
                'input.mp4',
                '-t',
                `${duration}`,
                '-c',
                'copy',
                'output.mp4'
            ]);

            const data = (await ffmpeg.readFile('output.mp4')) as Uint8Array;
            const trimmedBlob = new Blob([data.buffer as any], {
                type: 'video/mp4'
            });

            const trimmedFile = new File([trimmedBlob], 'trimmed-video.mp4', {
                type: 'video/mp4',
                lastModified: Date.now()
            });
            toast.info('영상이 업로드 되었습니다.');
            // TODO: 추후 리졸버 적용
            setTrimmedBlob(trimmedBlob);
            setTrimmedUrl(URL.createObjectURL(trimmedBlob));
        } catch (err) {
            console.error(err);
        } finally {
            setIsTrimming(false);
        }
    };

    const handleRadioButtons = useCallback(
        (radioKey: string, value: number) => {
            if (
                !value &&
                !window.confirm(
                    '사용중인 영상을 미사용으로 변경하시는 경우 플레이리스트에서 즉시 삭제 됩니다. 미사용으로 변경하시겠어요?'
                )
            ) {
                return;
            }
            // TODO: 추후 리졸버 적용
            try {
                setVideos((prev) => {
                    return {
                        ...prev,
                        [radioKey]: !!value
                    };
                });
                toast.success(
                    `영상이 ${value ? '사용중' : '미사용'}으로 변경되었습니다.`
                );
            } catch (err) {
                console.error(err);
            }
        },
        []
    );

    const onDelete = (id: number) => {
        if (
            !window.confirm(
                '해당 영상을 삭제하시면 복구가 불가능합니다. 정말 삭제하시겠어요?'
            )
        )
            return;
        try {
            toast.success('파일을 삭제했습니다.');
        } catch (err) {
            console.error(err);
        }
    };

    const onSubmit = () => {
        if (PLAYLIST_DATA.length !== Object.keys(videos).length)
            return toast.warning('사용 여부를 전부 선택해주세요.');
        // if (!window.confirm('플레이리스트를 저장하시겠습니까?')) return;
        try {
            toast.success('저장되었습니다.');
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <>
            <InfoSection title={`${videoType} 영상`} ROW>
                <StyledFileUpload
                    title="영상 추가"
                    subTitle="(mp4 만 가능)"
                    description="파일 크기는 최대 20MB까지 추가할 수 있습니다."
                    width={630}
                    margin="0 0 24px"
                    onChange={async (e) => {
                        onChangeFile(e);
                        await load();
                    }}
                    accept="video/mp4"
                />
                {isTrimming && <Loader />}
                <RegularFont
                    $margin="0 0 12px"
                    $fontColor={theme.colors.positiveBlue}
                >
                    플레이리스트에는 사용중인 이미지만 노출됩니다.
                </RegularFont>
                <E.SectionWrapper $width="630px">
                    <TableHeader
                        header={mediaListHeader}
                        bgColor={theme.colors.ultraLightGrayBorder}
                    />
                    <TableBody>
                        {PLAYLIST_DATA.length ? (
                            PLAYLIST_DATA.map((data, index) => {
                                return (
                                    <MediaListRow
                                        key={`${index}-video-list`}
                                        header={mediaListHeader}
                                        data={data}
                                        onChange={handleRadioButtons}
                                        onDelete={onDelete}
                                        radioGroup={videos}
                                        radioKey={`video${data.id}`}
                                        imgWidth={aspect > 1 ? 45 : 24}
                                        imgHeight={aspect >= 1 ? 24 : 45}
                                    />
                                );
                            })
                        ) : (
                            <EmptyTable>
                                재생할 플레이리스트가 없습니다.
                            </EmptyTable>
                        )}
                    </TableBody>
                </E.SectionWrapper>
            </InfoSection>
        </>
    );
};

export default VideoSetting;
